// ──────────────────────────────────────────────────────────────────────────
// PostHog analytics — hermetic, self-contained wrapper for the web client.
//
// Mirrors the mobile `AnalyticsService` (matchup-mobile/lib/services/
// analytics_service.dart) so event names and semantics stay in sync across
// clients. Everything here is defensive: if PostHog isn't configured (no key)
// or the SDK throws, every call silently no-ops and the app keeps working.
// Nothing outside this module needs to know about posthog-js.
// ──────────────────────────────────────────────────────────────────────────

import posthog from 'posthog-js';

/** Canonical analytics event names. Keep in sync with the mobile client. */
export const AnalyticsEvents = Object.freeze({
  // App lifecycle
  appOpened: 'app_opened',

  // Auth
  signInPhoneSubmitted: 'sign_in_phone_submitted',
  signInPhoneError: 'sign_in_phone_error',
  otpResendTapped: 'otp_resend_tapped',
  otpIncorrectCode: 'otp_incorrect_code',
  loginSuccess: 'login_success',
  logout: 'logout',

  // Navigation
  screenViewed: 'screen_viewed',

  // Matching
  onDemandMatchTriggered: 'on_demand_match_triggered',
});

let initialized = false;

const apiKey = process.env.REACT_APP_POSTHOG_KEY;
const apiHost = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

/** True once PostHog has been set up with a usable key. */
function ready() {
  return initialized && !!apiKey;
}

/**
 * Initialize the PostHog SDK. Safe to call multiple times — only the first
 * call with a configured key does anything. No-ops (without throwing) when no
 * key is present, e.g. local builds without analytics configured.
 */
export function initAnalytics() {
  if (initialized) return;
  if (!apiKey) {
    // Not configured — leave analytics disabled but don't break the app.
    return;
  }

  try {
    posthog.init(apiKey, {
      api_host: apiHost,
      // Only create person profiles for identified users (parity with mobile
      // `PostHogPersonProfiles.identifiedOnly`).
      person_profiles: 'identified_only',
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
      loaded: () => {
        try {
          posthog.register({ platform: 'web' });
        } catch (_) {}
      },
    });
    initialized = true;
  } catch (e) {
    // Never let analytics setup take down the app.
    // eslint-disable-next-line no-console
    console.warn('PostHog init failed:', e);
  }
}

// ──────────────────────────────────────────────
// Event tracking
// ──────────────────────────────────────────────

export function logEvent(eventName, properties) {
  if (!ready()) return;
  try {
    posthog.capture(eventName, properties || {});
  } catch (_) {}
}

export function logScreenView(screenName, properties) {
  logEvent(AnalyticsEvents.screenViewed, { screen_name: screenName, ...(properties || {}) });
}

// ──────────────────────────────────────────────
// User identification
// ──────────────────────────────────────────────

export function setUserId(userId, properties) {
  if (!ready() || !userId) return;
  try {
    posthog.identify(userId, properties || {});
  } catch (_) {}
}

export function setUserProperties(properties) {
  if (!ready() || !properties) return;
  try {
    posthog.setPersonProperties(properties);
  } catch (_) {}
}

// ──────────────────────────────────────────────
// Super properties (attached to every event)
// ──────────────────────────────────────────────

export function registerSuperProperty(key, value) {
  if (!ready()) return;
  try {
    posthog.register({ [key]: value });
  } catch (_) {}
}

// ──────────────────────────────────────────────
// Feature flags
// ──────────────────────────────────────────────

export function isFeatureEnabled(key) {
  if (!ready()) return false;
  try {
    return !!posthog.isFeatureEnabled(key);
  } catch (_) {
    return false;
  }
}

export function getFeatureFlag(key) {
  if (!ready()) return undefined;
  try {
    return posthog.getFeatureFlag(key);
  } catch (_) {
    return undefined;
  }
}

export function reloadFeatureFlags() {
  if (!ready()) return;
  try {
    posthog.reloadFeatureFlags();
  } catch (_) {}
}

// ──────────────────────────────────────────────
// Session management
// ──────────────────────────────────────────────

export function resetUser() {
  if (!ready()) return;
  try {
    posthog.reset();
  } catch (_) {}
}

const AnalyticsService = {
  initAnalytics,
  logEvent,
  logScreenView,
  setUserId,
  setUserProperties,
  registerSuperProperty,
  isFeatureEnabled,
  getFeatureFlag,
  reloadFeatureFlags,
  resetUser,
};

export default AnalyticsService;
